import { isAdmin, isConsumer, isKiosk, isPos } from 'src/config'
import ConfigurationAudit from 'src/models/configuration-audit/ConfigurationAudit'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, JsonArray, JsonObject, LocalizedJsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import UserClass from 'src/models/user-class/UserClass'

export const CHANNELS = {
  KIOSK: 'kiosk',
  REGISTER: 'register',
  WEBSITE: 'website'
}
CHANNELS.ALL = _.values(CHANNELS)
Object.freeze(CHANNELS)

export const TICKET_TYPE_REPORTING_OPTIONS = {
  BOX_OFFICE_REPORTING_NAME: 'box_office_reporting_name',
  DISCOUNT_REPORTING_NAME: 'discount_reporting_name',
  DISPLAY_NAME: 'display_name',
  REPORTING_NAME: 'reporting_name',
  VOUCHER_REPORTING_NAME: 'voucher_reporting_name'
}
TICKET_TYPE_REPORTING_OPTIONS.ALL = _.values(TICKET_TYPE_REPORTING_OPTIONS)
Object.freeze(TICKET_TYPE_REPORTING_OPTIONS)

export default class TicketType extends Model {
  static name = 'TicketType'

  static isConfiguration = true
  static duplicatable = true
  static paginate = true
  static deletable = true

  static versioned = true

  static hasAuditSummary = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Ticketing]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      userClass: {
        type: UserClass,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      membershipType: {
        type: MembershipType,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      excludeRatings: {
        type: JsonArray,
        default: () => ([])
      },
      ticketTypeOptions: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            has_custom_display_name: false,
            custom_display_name: null,
            has_custom_reporting_name: false,
            custom_reporting_name: null,
            reporting_priority: [
              TICKET_TYPE_REPORTING_OPTIONS.VOUCHER_REPORTING_NAME,
              TICKET_TYPE_REPORTING_OPTIONS.DISCOUNT_REPORTING_NAME,
              TICKET_TYPE_REPORTING_OPTIONS.REPORTING_NAME
            ],
            box_office_reporting_priority: [
              TICKET_TYPE_REPORTING_OPTIONS.VOUCHER_REPORTING_NAME,
              TICKET_TYPE_REPORTING_OPTIONS.DISCOUNT_REPORTING_NAME,
              TICKET_TYPE_REPORTING_OPTIONS.REPORTING_NAME
            ],
            has_custom_box_office_reporting_name: false,
            custom_box_office_reporting_name: null,
            applies_to_specific_seats: false,
            report_price_after_voucher: false,
            report_price_after_discount: false,
            has_max_reported_price: null,
            max_reported_price: 0,
            max_reported_price_cents: 0,
            has_min_reported_price: false,
            min_reported_price: 0,
            min_reported_price_cents: 0,
            requires_authorization: false
          }
        },
        translatables: ['custom_display_name']
      },
      channels: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          kiosk: true,
          register: true,
          website: true
        })
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      excludeFromReporting: {
        type: Boolean,
        default: false
      },
      seatChartAreaCategoryIds: {
        type: [ID],
        default: () => ([]),
        excludeFromDefault: true
      },
      isCeaCard: {
        type: Boolean,
        default: false
      },
      isDboxTicketTypeValue: {
        type: Boolean,
        default: false
      },
      modelAudits: {
        type: [ConfigurationAudit],
        default: () => { return [] },
        output: true,
        excludeFromDefault: !isAdmin
      },
      factor: {
        type: Number,
        input: true
      }
    }

    this.computed = {
      displayName () {
        if (this.hasCustomDisplayName && this.customDisplayName && this.customDisplayName.length > 0) {
          return this.customDisplayName
        } else {
          return this.name
        }
      },
      reportingName () {
        if (this.hasCustomReportingName && this.customReportingName && this.customReportingName.length > 0) {
          return this.customReportingName
        } else {
          return this.name
        }
      },
      boxOfficeReportingName () {
        if (this.hasCustomBoxOfficeReportingName && this.customBoxOfficeReportingName && this.customBoxOfficeReportingName.length > 0) {
          return this.customBoxOfficeReportingName
        } else {
          return this.name
        }
      },
      factorPrice () {
        return this.price * (this.factor || 1)
      }
    }
  }

  get isAvailable () {
    if (isPos) {
      return this.channels.register
    } else if (isConsumer) {
      return this.channels.website
    } else if (isKiosk) {
      return this.channels.kiosk
    } else {
      return true
    }
  }

  get availableOnline () {
    return this.channels.website
  }

  get availableAtStations () {
    return this.channels.register
  }

  get hasCustomReportingName () {
    return this.ticketTypeOptions.has_custom_reporting_name
  }

  get customReportingName () {
    return this.ticketTypeOptions.custom_reporting_name
  }

  get hasCustomBoxOfficeReportingName () {
    return this.ticketTypeOptions.has_custom_box_office_reporting_name
  }

  get customBoxOfficeReportingName () {
    return this.ticketTypeOptions.custom_box_office_reporting_name
  }

  get hasCustomDisplayName () {
    return this.ticketTypeOptions.has_custom_display_name
  }

  get customDisplayName () {
    return this.ticketTypeOptions.custom_display_name
  }

  get appliesToSpecificSeats () {
    return this.ticketTypeOptions.applies_to_specific_seats
  }

  get requiresAuthorization () {
    return this.ticketTypeOptions.requires_authorization
  }

  get description () {
    if (this.price === 0) {
      return `${this.displayName} (Free)`
    } else {
      return `${this.displayName} (${this.i18n.n(this.price, 'currency')})`
    }
  }

  isMembershipTypeMatch (membershipTypeId) {
    if (!membershipTypeId) return false
    return this.membershipType?.id === membershipTypeId
  }

  isUserClassMatch (userClassId) {
    if (!userClassId) return false
    return this.userClass?.id === userClassId
  }
}
